import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { Team } from '../logic/team';
import type { PlayerStats } from './statsPanel';
import { COLORS, textStyle, drawBeveledButton, BUTTON_THEMES } from './theme';

export interface GameOverData {
  winner: Team | null;
  teams: Team[];
  stats?: PlayerStats[];
  duration?: number;
  totalTurns?: number;
}

export class GameOverScreen {
  container: Container;
  private background: Graphics;
  private titleText: Text;
  private winnerText: Text;
  private statsContainer: Container;
  private highlightsContainer: Container;
  private restartButton: Graphics;
  private restartText: Text;
  private quitButton: Graphics;
  private quitText: Text;
  private visible: boolean = false;
  private onRestart?: () => void;
  private onQuit?: () => void;
  private width: number;
  private height: number;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.container = new Container();
    this.container.visible = false;

    this.background = new Graphics();
    this.background.rect(0, 0, this.width, this.height);
    this.background.fill({ color: 0x06101f, alpha: 0.86 });
    this.container.addChild(this.background);

    this.titleText = new Text({
      text: 'GAME OVER',
      style: textStyle({ size: 52, display: true, color: COLORS.gold, outline: 5 }),
    });
    this.titleText.anchor.set(0.5, 0.5);
    this.titleText.position.set(this.width / 2, 64);
    this.container.addChild(this.titleText);

    this.winnerText = new Text({
      text: '',
      style: textStyle({ size: 28, display: true, color: COLORS.gold }),
    });
    this.winnerText.anchor.set(0.5, 0.5);
    this.winnerText.position.set(this.width / 2, 110);
    this.container.addChild(this.winnerText);

    this.highlightsContainer = new Container();
    this.highlightsContainer.position.set(this.width / 2, 160);
    this.container.addChild(this.highlightsContainer);

    this.statsContainer = new Container();
    this.statsContainer.position.set(this.width / 2, 280);
    this.container.addChild(this.statsContainer);

    const buttonStyle = textStyle({ size: 20, weight: '700' });

    this.restartButton = new Graphics();
    drawBeveledButton(this.restartButton, -90, -22, 180, 44, BUTTON_THEMES.green, 12);
    this.restartButton.position.set(this.width / 2 - 100, this.height - 60);
    this.restartButton.eventMode = 'static';
    this.restartButton.cursor = 'pointer';
    this.restartButton.on('pointerdown', () => this.handleRestart());
    this.restartButton.on('pointerover', () => {
      this.restartButton.tint = 0xaaffaa;
    });
    this.restartButton.on('pointerout', () => {
      this.restartButton.tint = 0xffffff;
    });
    this.container.addChild(this.restartButton);

    this.restartText = new Text({ text: 'PLAY AGAIN', style: buttonStyle });
    this.restartText.anchor.set(0.5, 0.5);
    this.restartText.position.set(this.width / 2 - 100, this.height - 60);
    this.container.addChild(this.restartText);

    this.quitButton = new Graphics();
    drawBeveledButton(this.quitButton, -90, -22, 180, 44, BUTTON_THEMES.slate, 12);
    this.quitButton.position.set(this.width / 2 + 100, this.height - 60);
    this.quitButton.eventMode = 'static';
    this.quitButton.cursor = 'pointer';
    this.quitButton.on('pointerdown', () => this.handleQuit());
    this.quitButton.on('pointerover', () => {
      this.quitButton.tint = 0xcccccc;
    });
    this.quitButton.on('pointerout', () => {
      this.quitButton.tint = 0xffffff;
    });
    this.container.addChild(this.quitButton);

    this.quitText = new Text({ text: 'QUIT TO LOBBY', style: buttonStyle });
    this.quitText.anchor.set(0.5, 0.5);
    this.quitText.position.set(this.width / 2 + 100, this.height - 60);
    this.container.addChild(this.quitText);
  }

  show(
    winner: Team | null,
    teams: Team[],
    stats?: PlayerStats[],
    duration?: number,
    totalTurns?: number,
  ): void {
    this.visible = true;
    this.container.visible = true;

    if (winner) {
      const allies = teams.filter((t) => t.allianceId === winner.allianceId);
      const label = allies.length > 1 ? allies.map((t) => t.name).join(' + ') : winner.name;
      this.titleText.text = '🏆 VICTORY! 🏆';
      this.titleText.style.fill = `#${winner.color.toString(16).padStart(6, '0')}`;
      this.winnerText.text = `${label} Win${allies.length > 1 ? '' : 's'}!`;
      this.winnerText.style.fill = `#${winner.color.toString(16).padStart(6, '0')}`;
    } else {
      this.titleText.text = 'DRAW!';
      this.titleText.style.fill = '#ffffff';
      this.winnerText.text = 'No survivors...';
      this.winnerText.style.fill = '#999999';
    }

    this.highlightsContainer.removeChildren();
    this.statsContainer.removeChildren();

    if (stats && stats.length > 0) {
      this.renderHighlights(stats);
      this.renderStatsTable(stats);
    } else {
      this.renderTeamSurvival(teams);
    }

    if (duration !== undefined || totalTurns !== undefined) {
      this.renderMatchInfo(duration, totalTurns);
    }
  }

  private renderHighlights(stats: PlayerStats[]): void {
    const highlightStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 14,
      fill: '#aaaaaa',
    });
    const valueStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 16,
      fill: '#ffffff',
      fontWeight: 'bold',
    });

    const mvp = stats.reduce((max, s) => (s.damageDealt > max.damageDealt ? s : max), stats[0]);
    const topKiller = stats.reduce((max, s) => (s.kills > max.kills ? s : max), stats[0]);
    const bestAccuracy = stats.reduce((max, s) => {
      const acc = s.shotsFired > 0 ? s.shotsHit / s.shotsFired : 0;
      const maxAcc = max.shotsFired > 0 ? max.shotsHit / max.shotsFired : 0;
      return acc > maxAcc ? s : max;
    }, stats[0]);

    const highlights = [
      {
        label: '👑 MVP (Most Damage)',
        value: `${mvp.playerName} - ${mvp.damageDealt} dmg`,
        color: '#ffd700',
      },
      {
        label: '💀 Top Killer',
        value: `${topKiller.playerName} - ${topKiller.kills} kills`,
        color: '#ff4444',
      },
      {
        label: '🎯 Best Accuracy',
        value: `${bestAccuracy.playerName} - ${bestAccuracy.shotsFired > 0 ? Math.round((bestAccuracy.shotsHit / bestAccuracy.shotsFired) * 100) : 0}%`,
        color: '#44aaff',
      },
    ];

    let xOffset = -200;
    for (const h of highlights) {
      const labelText = new Text({ text: h.label, style: highlightStyle });
      labelText.anchor.set(0.5, 0);
      labelText.position.set(xOffset, 0);
      this.highlightsContainer.addChild(labelText);

      const valText = new Text({ text: h.value, style: { ...valueStyle, fill: h.color } });
      valText.anchor.set(0.5, 0);
      valText.position.set(xOffset, 18);
      this.highlightsContainer.addChild(valText);

      xOffset += 200;
    }
  }

  private renderStatsTable(stats: PlayerStats[]): void {
    const headerStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 14,
      fill: '#888888',
      fontWeight: 'bold',
    });
    const rowStyle = new TextStyle({ fontFamily: 'Arial', fontSize: 14, fill: '#ffffff' });

    const sortedStats = [...stats].sort((a, b) => b.damageDealt - a.damageDealt);
    const columns = ['Player', 'Damage', 'Kills', 'Deaths', 'Accuracy'];
    const colWidths = [120, 70, 60, 60, 70];
    const startX = -(colWidths.reduce((a, b) => a + b, 0) / 2);

    let xPos = startX;
    for (let i = 0; i < columns.length; i++) {
      const header = new Text({ text: columns[i], style: headerStyle });
      header.anchor.set(0, 0);
      header.position.set(xPos, 0);
      this.statsContainer.addChild(header);
      xPos += colWidths[i];
    }

    let yOffset = 25;
    for (let idx = 0; idx < sortedStats.length; idx++) {
      const s = sortedStats[idx];
      const isMvp = idx === 0;
      const accuracy = s.shotsFired > 0 ? Math.round((s.shotsHit / s.shotsFired) * 100) : 0;
      const rowData = [
        isMvp ? `👑 ${s.playerName}` : s.playerName,
        String(s.damageDealt),
        String(s.kills),
        String(s.deaths),
        `${accuracy}%`,
      ];
      const rowColors = [isMvp ? '#ffd700' : '#ffffff', '#ff4444', '#44ff44', '#ff8844', '#44aaff'];

      xPos = startX;
      for (let i = 0; i < rowData.length; i++) {
        const cell = new Text({ text: rowData[i], style: { ...rowStyle, fill: rowColors[i] } });
        cell.anchor.set(0, 0);
        cell.position.set(xPos, yOffset);
        this.statsContainer.addChild(cell);
        xPos += colWidths[i];
      }
      yOffset += 22;
    }
  }

  private renderTeamSurvival(teams: Team[]): void {
    const statsStyle = new TextStyle({ fontFamily: 'Arial', fontSize: 16, fill: '#cccccc' });

    let yOffset = 0;
    for (const team of teams) {
      const alive = team.worms.filter((w) => w.state !== 'dead').length;
      const total = team.worms.length;

      const statText = new Text({
        text: `${team.name}: ${alive}/${total} survived`,
        style: { ...statsStyle, fill: `#${team.color.toString(16).padStart(6, '0')}` },
      });
      statText.anchor.set(0.5, 0);
      statText.position.set(0, yOffset);
      this.statsContainer.addChild(statText);
      yOffset += 25;
    }
  }

  private renderMatchInfo(duration?: number, totalTurns?: number): void {
    const infoStyle = new TextStyle({ fontFamily: 'Arial', fontSize: 12, fill: '#666666' });
    const parts: string[] = [];

    if (duration !== undefined) {
      const mins = Math.floor(duration / 60);
      const secs = Math.floor(duration % 60);
      parts.push(`Duration: ${mins}:${secs.toString().padStart(2, '0')}`);
    }
    if (totalTurns !== undefined) {
      parts.push(`Turns: ${totalTurns}`);
    }

    if (parts.length > 0) {
      const infoText = new Text({ text: parts.join('  |  '), style: infoStyle });
      infoText.anchor.set(0.5, 0);
      infoText.position.set(
        0,
        this.statsContainer.children.length > 0
          ? Math.max(...this.statsContainer.children.map((c) => c.position.y)) + 40
          : 0,
      );
      this.statsContainer.addChild(infoText);
    }
  }

  hide(): void {
    this.visible = false;
    this.container.visible = false;
  }

  isVisible(): boolean {
    return this.visible;
  }

  setOnRestart(callback: () => void): void {
    this.onRestart = callback;
  }

  setOnQuit(callback: () => void): void {
    this.onQuit = callback;
  }

  private handleRestart(): void {
    this.hide();
    this.onRestart?.();
  }

  private handleQuit(): void {
    this.hide();
    this.onQuit?.();
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;

    this.background.clear();
    this.background.rect(0, 0, width, height);
    this.background.fill({ color: 0x06101f, alpha: 0.86 });

    this.titleText.position.set(width / 2, 64);
    this.winnerText.position.set(width / 2, 110);
    this.highlightsContainer.position.set(width / 2, 160);
    this.statsContainer.position.set(width / 2, 280);
    this.restartButton.position.set(width / 2 - 100, height - 60);
    this.restartText.position.set(width / 2 - 100, height - 60);
    this.quitButton.position.set(width / 2 + 100, height - 60);
    this.quitText.position.set(width / 2 + 100, height - 60);
  }
}
